import React, { PureComponent } from 'react';
import { MonitoredRegulationOutlook } from 'app/model/SOL49';
import { Flex } from 'app/components/common/Flex';
import { Select, Tag } from 'antd';
import { FilterFilled, TableOutlined, UnorderedListOutlined } from '@ant-design/icons';
import ConditionalRender from 'app/common/components/ConditionalRender';
import { Sorting } from '@devexpress/dx-react-grid';
import RegulationOutlookTable, {
    REGULATION_OUTLOOK_ACTIONS_COLUMN,
    REGULATION_OUTLOOK_SELECTED_COLUMN,
    RegulationOutlookColumn,
    StandardRegulationOutlookColumn,
} from 'app/components/table/RegulationOutlookTable';
import './RegulationOutlooks.less';
import { FlowGrid } from 'app/components/dashboard/network/flows/FlowGrid';
import { RegulationOutlookLoad } from 'app/components/dashboard/network/flows/RegulationOutlookLoad';
import Utils from 'app/common/util/Utils';
import { MULTIPLE_SELECTION, MultipleSelection } from 'app/common/table/ManagedTable';
import { SelectionLabel } from 'app/components/common/SelectionLabel';
import memoizeOne from 'memoize-one';
import { ClosablePopover } from 'app/components/common/ClosablePopover';
import {
    DEFAULT_REGULATION_OUTLOOK_SETTINGS,
    REGULATION_OUTLOOK_REASONS,
    RegulationOutlookSettings,
    RegulationOutlookSettingsContent,
    RegulationOutlookSettingsTitle,
} from 'app/components/dashboard/detailed/regOutlook/RegulationOutlookSettings';
import MeasureUtils from 'app/common/util/MeasureUtils';

const REGULATION_OUTLOOK_LIST_COLUMNS: RegulationOutlookColumn[] = [
    REGULATION_OUTLOOK_SELECTED_COLUMN,
    StandardRegulationOutlookColumn.AERODROME,
    StandardRegulationOutlookColumn.PREDICTION,
    StandardRegulationOutlookColumn.WEATHER_REASON,
    StandardRegulationOutlookColumn.ATC_CAPACITY_REASON,
    StandardRegulationOutlookColumn.AD_CAPACITY_REASON,
    StandardRegulationOutlookColumn.STRIKE_REASON,
    StandardRegulationOutlookColumn.EVENTS_REASON,
    StandardRegulationOutlookColumn.OTHER_REASON,
    REGULATION_OUTLOOK_ACTIONS_COLUMN,
];

interface RegulationOutlooksProps {
    regOutlook: MonitoredRegulationOutlook[];
    selection: MonitoredRegulationOutlook[];
    onSelectionChange: (selection: MonitoredRegulationOutlook[]) => void;
    currentTime: Date;
    textFilter: string;
}

interface RegulationOutlooksState {
    gridDisplay: boolean;
    sortBy: 'name' | 'prediction';

    settings: RegulationOutlookSettings;

    sorting: Sorting[];
}

export class RegulationOutlooks extends PureComponent<RegulationOutlooksProps, RegulationOutlooksState> {
    private settingsPopoverRef = React.createRef<ClosablePopover>();

    constructor(props: RegulationOutlooksProps) {
        super(props);

        this.state = {
            gridDisplay: true,
            sortBy: 'prediction',
            sorting: [{ columnName: 'PREDICTION', direction: 'desc' }],
            settings: DEFAULT_REGULATION_OUTLOOK_SETTINGS,
        };
    }

    render() {
        const filtered = this.getFilteredRegOutlooks(this.props.regOutlook, this.props.textFilter, this.state.settings);
        const nbOutlooks = Utils.size(filtered) || 0;
        const nbSelected = this.props.selection?.length || 0;
        const selectionLabelVisible = nbSelected > 0;

        return (
            <Flex direction="vertical" className="regulation-outlooks overflow detailed-subpanel">
                <Flex className="detailed-subpanel-header" size="2em">
                    <Flex className="detailed-subpanel-header-left" size={'20%'}>
                        <SelectionLabel
                            visible={selectionLabelVisible}
                            title={`${nbSelected} outlook selected`}
                            subTitle={`${nbOutlooks} in total`}
                            onSelectAll={this.handleSelectAll(filtered)}
                            onClearSelection={() => this.props.onSelectionChange([])}
                            children={this.renderRegOutlookSelectionInfo(nbOutlooks)}
                        />
                    </Flex>
                    <Flex className="detailed-subpanel-header-right" size={'80%'}>
                        <Flex style={{ flexWrap: 'wrap', rowGap: '0.5em' }} grow={false}>
                            {this.renderFilterTags()}
                        </Flex>
                        <ConditionalRender condition={this.state.gridDisplay}>
                            <Select value={this.state.sortBy} onChange={value => this.setState({ sortBy: value })}>
                                <Select.Option value="prediction">Prediction</Select.Option>
                                <Select.Option value="name">Name</Select.Option>
                            </Select>
                        </ConditionalRender>
                        <span className="detailed-subpanel-header-right" style={{ display: 'flex' }}>
                            <ConditionalRender condition={this.state.gridDisplay}>
                                <UnorderedListOutlined onClick={this.switchDisplayMode} />
                            </ConditionalRender>
                            <ConditionalRender condition={!this.state.gridDisplay}>
                                <TableOutlined onClick={this.switchDisplayMode} />
                            </ConditionalRender>
                            <ClosablePopover
                                key="settings-action"
                                ref={this.settingsPopoverRef}
                                trigger="click"
                                placement="bottom"
                                title={<RegulationOutlookSettingsTitle />}
                                content={
                                    <RegulationOutlookSettingsContent
                                        settings={this.state.settings}
                                        onChange={this.onFilterSettingsChange}
                                        onClose={() => this.settingsPopoverRef.current.hide()}
                                    />
                                }>
                                <FilterFilled onClick={() => this.settingsPopoverRef.current.show()} />
                            </ClosablePopover>
                        </span>
                    </Flex>
                </Flex>
                <ConditionalRender condition={this.state.gridDisplay}>
                    {this.renderRegulationOutlookGrid(filtered)}
                </ConditionalRender>
                <ConditionalRender condition={!this.state.gridDisplay}>
                    {this.renderRegulationOutlookTable(filtered)}
                </ConditionalRender>
            </Flex>
        );
    }

    private onFilterSettingsChange = (settings: RegulationOutlookSettings) => {
        this.setState({ settings });
        this.settingsPopoverRef.current.hide();
    };

    private renderFilterTags = () => [
        <ConditionalRender key="min-prediction" condition={this.state.settings.minPrediction > 0}>
            <Tag
                closable
                onClose={() =>
                    this.setState({
                        settings: {
                            ...this.state.settings,
                            minPrediction: 0,
                        },
                    })
                }>{`Min prediction: ${this.state.settings.minPrediction * 100}%`}</Tag>
        </ConditionalRender>,
        REGULATION_OUTLOOK_REASONS.filter(reason => !this.state.settings.reasons.includes(reason)).map(reason => {
            return (
                <Tag
                    key={reason}
                    style={{ textDecoration: 'line-through' }}
                    closable
                    onClose={() =>
                        this.setState({
                            settings: {
                                ...this.state.settings,
                                reasons: [...this.state.settings.reasons, reason],
                            },
                        })
                    }>
                    {MeasureUtils.toRegulationOutlookReasonString(reason)}
                </Tag>
            );
        }),
    ];

    private getFilteredRegOutlooks = memoizeOne(
        (regOutlook: MonitoredRegulationOutlook[], textFilter: string, settings: RegulationOutlookSettings) => {
            if (!regOutlook) return [];

            let filtered = regOutlook;
            if (textFilter) {
                const upperCasetextFilter = Utils.toUpperCase(textFilter).split(' ');
                filtered = filtered.filter(regOutlook => {
                    const aerodromeId = Utils.toUpperCase(regOutlook.regulationOutlook.aerodromeId);
                    const matchingTextFilters = upperCasetextFilter.filter(textFilterEl => {
                        const re = Utils.wildcardToRegex(textFilterEl);
                        return re.test(aerodromeId);
                    });
                    return matchingTextFilters.length > 0;
                });
            }

            if (settings) {
                filtered = filtered.filter(regOutlook => {
                    const worstReason = regOutlook.regulationOutlook?.reasons.reduce((prev, curr) =>
                        prev.prediction > curr.prediction ? prev : curr,
                    );
                    return (
                        regOutlook.regulationOutlook.prediction >= settings.minPrediction &&
                        settings.reasons.includes(worstReason.reason)
                    );
                });
            }

            return filtered;
        },
    );

    sortingAlgorithm = () => {
        switch (this.state.sortBy) {
            case 'name':
                return (a: MonitoredRegulationOutlook, b: MonitoredRegulationOutlook) =>
                    a.regulationOutlook.aerodromeId.localeCompare(b.regulationOutlook.aerodromeId);
            case 'prediction':
                return (a: MonitoredRegulationOutlook, b: MonitoredRegulationOutlook) =>
                    b.regulationOutlook.prediction - a.regulationOutlook.prediction;
        }
    };

    renderRegulationOutlookGrid = (regOutlooks: MonitoredRegulationOutlook[]) => {
        const selectedAerodromes = this.props.selection?.map(regOutlook => regOutlook.regulationOutlook.aerodromeId);
        return (
            <Flex className="content overflow" grow={false}>
                <FlowGrid columns={4}>
                    {regOutlooks
                        .slice()
                        .sort(this.sortingAlgorithm())
                        .map(mro => {
                            const aerodrome = mro.regulationOutlook.aerodromeId;
                            const selected = Utils.contains(selectedAerodromes, aerodrome);
                            return (
                                <RegulationOutlookLoad
                                    key={`regulation-outlook-load-${aerodrome}`}
                                    monitoredRegulationOutlook={mro}
                                    currentTime={this.props.currentTime}
                                    selected={selected}
                                    onSelectionChange={selected => this.handleSelectionUpdate(mro, selected)}
                                />
                            );
                        })}
                </FlowGrid>
            </Flex>
        );
    };

    renderRegulationOutlookTable = (regOutlooks: MonitoredRegulationOutlook[]) => {
        return (
            <Flex className="regulation-outlook-table content">
                <RegulationOutlookTable
                    data={regOutlooks}
                    columns={REGULATION_OUTLOOK_LIST_COLUMNS}
                    columnResizeDisabled={true}
                    selection={this.selection(this.props.selection)}
                />
            </Flex>
        );
    };

    renderRegOutlookSelectionInfo = (nbOutlooks: number) => (
        <div>
            <span className="detailed-subpanel-title">Regulation outlook</span>
            <span className="detailed-subpanel-title-info">{nbOutlooks}</span>
        </div>
    );

    switchDisplayMode = () => this.setState({ gridDisplay: !this.state.gridDisplay });

    selection = memoizeOne(
        (selected: MonitoredRegulationOutlook[]): MultipleSelection<MonitoredRegulationOutlook> => ({
            selectionMode: MULTIPLE_SELECTION,
            onSelectionChange: this.props.onSelectionChange,
            selectedItems: selected,
        }),
    );

    handleSelectAll = (selection: MonitoredRegulationOutlook[]) => {
        const newSelection = Utils.copyAndAddAllIfNeeded(this.props.selection, selection);
        return () => this.props.onSelectionChange?.(newSelection);
    };

    handleSelectionUpdate = (regOutlook: MonitoredRegulationOutlook, selected: boolean) => {
        const newSelection = selected
            ? Utils.copyAndAddIfNeeded(this.props.selection, regOutlook)
            : Utils.copyAndRemoveIfNeeded(this.props.selection, regOutlook);
        this.props.onSelectionChange(newSelection);
    };
}
